import { useState } from 'react'
import {
  CollectionReference,
  QueryDocumentSnapshot,
  collection,
  getDocs,
  getFirestore,
  query,
  where,
  QueryConstraint,
} from 'firebase/firestore'
import { useToast } from '@chakra-ui/react'
import FirestoreUser from '../../../../types/FirestoreUser'
import useHandleError from '../../../../hooks/useHandleError'
import { ReportType } from '../types'
import { DATE_REQUIRED_REPORTS } from '../utils/reportConstants'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

/**
 * Custom hook for generating user reports
 * Handles report type selection, date range, and data fetching
 *
 * @returns Object containing report state and generateReport function
 */
export function useReportGeneration() {
  const [reportType, setReportType] = useState<ReportType | ''>('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [hasGenerated, setHasGenerated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [docs, setDocs] = useState<QueryDocumentSnapshot<FirestoreUser>[]>([])
  const handleError = useHandleError()
  const toast = useToast()

  async function generateReport() {
    setHasGenerated(true)
    if (!reportType) {
      toast({
        status: 'error',
        description: 'Please select a report type.',
      })
      return
    }

    // Only require dates for reports that need them
    if (
      DATE_REQUIRED_REPORTS.has(reportType as ReportType) &&
      (!startDate || !endDate)
    ) {
      toast({
        status: 'error',
        description: 'Please select a start and end date.',
      })
      return
    }

    setLoading(true)
    setDocs([])

    try {
      const constraints: QueryConstraint[] = []

      if (reportType === 'uploadedDiagnosis') {
        // Query for users who have diagnosis documentation
        constraints.push(where('profile.diagnosisDocumentation', '!=', null))
      }

      if (reportType === 'missingEmergencyContact') {
        // Narrow down by canonical diagnosis values server-side to reduce scan (must be <= 10 values for Firestore 'in')
        constraints.push(
          where('profile.diagnosis', 'in', [
            'pls',
            'als',
            'als-probable',
            'other',
          ])
        )
      }

      const snap = await getDocs(
        query(
          collection(
            primaryServices.firestore,
            'users'
          ) as CollectionReference<FirestoreUser>,
          ...constraints
        )
      )

      // Client-side filtering
      let filteredDocs = snap.docs

      if (reportType === 'uploadedDiagnosis') {
        // Filter by date range on the client side
        const startDateTime = new Date(startDate).getTime()
        const endDateTime =
          new Date(endDate).getTime() + 24 * 60 * 60 * 1000 - 1 // Include the whole end day

        filteredDocs = snap.docs.filter((doc) => {
          const userData = doc.data()
          if (userData.profile?.diagnosisDocumentation) {
            // Check if any document was uploaded within the date range
            return userData.profile.diagnosisDocumentation.some((docItem) => {
              // Only include documents with upload dates (new format)
              if (typeof docItem === 'object' && docItem.uploadedAt) {
                const uploadDate = new Date(docItem.uploadedAt).getTime()
                return uploadDate >= startDateTime && uploadDate <= endDateTime
              }
              // Exclude documents without upload dates (old format)
              return false
            })
          }
          return false
        })
      }

      if (reportType === 'missingEmergencyContact') {
        // We already narrowed by diagnosis on the server where possible. Now filter for missing emergencyContactFirstName
        // and normalize diagnosis values to strictly match the canonical allowed set (pls | als | als-probable | other).
        const allowed = new Set(['pls', 'als', 'als-probable', 'other'])

        filteredDocs = snap.docs.filter((doc) => {
          const userData = doc.data()
          const diagRaw = userData.profile?.diagnosis
          const diagNorm = diagRaw ? String(diagRaw).toLowerCase().trim() : ''
          const matchesDiagnosis = allowed.has(diagNorm)

          // Consider emergency contact present only if it is a non-empty string after trimming
          const ecName = userData.profile?.emergencyContactFirstName
          const hasEmergencyContact =
            typeof ecName === 'string' && ecName.trim().length > 0
          const missingEmergencyContact = !hasEmergencyContact

          return matchesDiagnosis && missingEmergencyContact
        })
      }

      setDocs(filteredDocs)

      toast({
        status: 'success',
        description: `Report generated successfully. Found ${filteredDocs.length} users.`,
      })
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  return {
    reportType,
    setReportType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    hasGenerated,
    setHasGenerated,
    loading,
    docs,
    generateReport,
  }
}
